import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import { format, subYears } from 'date-fns'
import {
  downloadBlob, maskCnpj, truncateString, formatCpf, formatPhone, formatCurrencyInt,
} from '@core/utils/utils'

export default function useMonthlyProfessionalWorkplaceReport() {
  const now = new Date()
  const currentYear = format(now, 'yyyy')
  const currentMonth = format(now, 'MM')

  const filterYear = ref(currentYear)
  const filterMonth = ref(currentMonth)
  const institutionId = ref(null)
  const institutionOptions = ref([])

  const loading = ref(false)
  const items = ref([])

  const yearOptions = (new Array(4)).fill(0).map((_, i) => {
    const year = format(subYears(now, i), 'yyyy')
    return { label: year, value: year }
  }).filter(({ value }) => value > '2020')

  const allMonthOptions = [
    { label: 'Janeiro', value: '01' },
    { label: 'Fevereiro', value: '02' },
    { label: 'Março', value: '03' },
    { label: 'Abril', value: '04' },
    { label: 'Maio', value: '05' },
    { label: 'Junho', value: '06' },
    { label: 'Julho', value: '07' },
    { label: 'Agosto', value: '08' },
    { label: 'Setembro', value: '09' },
    { label: 'Outubro', value: '10' },
    { label: 'Novembro', value: '11' },
    { label: 'Dezembro', value: '12' },
  ]

  const monthOptions = computed(() => (
    filterYear.value === currentYear
      ? allMonthOptions.filter(({ value }) => value <= currentMonth)
      : allMonthOptions
  ))

  watch(filterYear, value => {
    if (value === currentYear && filterMonth.value > currentMonth) {
      filterMonth.value = currentMonth
    }
  })

  const formatWorkplaceDate = v => (v ? format(new Date(v), 'dd/MM/yy HH:mm') : '-')
  const formatCompetence = c => {
    const [year, month] = c.split('-')
    return `${month}/${year}`
  }

  const tableColumns = [
    { key: 'institution_name', label: 'Instituição' },
    { key: 'workplace_name', label: 'Unidade' },
    { key: 'physician_name', label: 'Profissional' },
    { key: 'crm', label: 'CRM' },
    { key: 'phone', label: 'Contato', formatter: v => (v ? formatPhone(v) : '-') },
    { key: 'status', label: 'Status' },
    {
      key: 'workplace_first_advance_at', label: '1ª Antecipação na Unidade', formatter: formatWorkplaceDate,
    },
    {
      key: 'workplace_last_advance_at', label: 'Última Antecipação na Unidade', formatter: formatWorkplaceDate,
    },
    { key: 'anticipations_current_month', label: 'Antecipações Mês Atual' },
    { key: 'anticipations_previous_month', label: 'Antecipações Mês Anterior' },
    { key: 'anticipations_two_months_ago', label: 'Antecipações Mês Retrasado' },
    { key: 'recurrence', label: 'Recorrência' },
  ]

  const getClassificationColor = color => {
    const colors = {
      red: '#df7273',
      yellow: '#ffb976',
      green: '#48da89',
    }
    return colors[color]
  }

  const getParams = () => ({
    competence: `${filterYear.value}-${filterMonth.value}`,
    institution: institutionId.value ? institutionId.value.value : undefined,
  })

  const fetchInstitutions = () => {
    store.dispatch('app-reports/fetchInstitutions', { perPage: 99999999 })
      .then(response => {
        institutionOptions.value = response.data.data.map(inst => ({
          value: inst.id,
          label: `${inst.name} | ${maskCnpj(inst.cnpj)}`,
        }))
      })
  }

  const loadReport = () => {
    loading.value = true
    store.dispatch('app-reports/monthlyProfessionalWorkplaceReport', getParams())
      .then(response => {
        // console.log(response)
        // items.value = {
        //   message: 'success',
        //   error: false,
        //   data: [
        //     {
        //       workplace_id: '1974e47a-bdf0-4de0-bda9-8a094a16cd60',
        //       institution_name: 'ClienteMed',
        //       workplace_name: 'Upa Sim',
        //       physician_name: 'Jeferson Marques Felix Filho',
        //       cpf: '03380397598',
        //       crm_number: '123',
        //       crm_state: 'BA',
        //       phone: '7592007395',
        //       classification_color: 'yellow',
        //       classification_label: 'Ativo e n\u00e3o adiantou nos \u00faltimos 30 dias.',
        //       workplace_first_advance_at: '2024-09-12 22:49:08+00',
        //       workplace_last_advance_at: '2026-06-09 17:46:25+00',
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 1,
        //         value: 66960,
        //         breakdown: [
        //           {
        //             competence: '2026-06',
        //             count: 1,
        //             value: 66960,
        //           },
        //         ],
        //       },
        //       recurrence: 'Recorrente',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'CLAUDINEIA GOMES PEREIRA',
        //       cpf: '09303233786',
        //       crm_number: '011966',
        //       crm_state: 'ES',
        //       phone: '27996423300',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'LORENA MARQUEZ RIZZI',
        //       cpf: '12121167714',
        //       crm_number: '17383',
        //       crm_state: 'ES',
        //       phone: '27999899195',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'JANAINA PEREIRA DA SILVA TOLEDO',
        //       cpf: '12426017759',
        //       crm_number: '12045',
        //       crm_state: 'ES',
        //       phone: '27999388100',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'FERNANDA DE CASTRO ROQUE',
        //       cpf: '10677235798',
        //       crm_number: '012140',
        //       crm_state: 'ES',
        //       phone: '27992966451',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'NATALIA VENTURIM MONERAT FAGUNDES',
        //       cpf: '13298520799',
        //       crm_number: '0013510',
        //       crm_state: 'ES',
        //       phone: '27998465972',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'CLARICE DE ALMEIDA FIORILLO',
        //       cpf: '10560942737',
        //       crm_number: '10799',
        //       crm_state: 'ES',
        //       phone: '27999391985',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'TAYANA AGOSTINHO AYUB',
        //       cpf: '11458710700',
        //       crm_number: '0014517',
        //       crm_state: 'ES',
        //       phone: '27981516877',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'JULIA CAROLINY NOGUEIRA BORGES LIMA',
        //       cpf: '06620240526',
        //       crm_number: '17502',
        //       crm_state: 'ES',
        //       phone: '27999404679',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'CAROLINA PASSOS ARRIGONI',
        //       cpf: '10198786735',
        //       crm_number: '014750',
        //       crm_state: 'ES',
        //       phone: '27998091844',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'LAIS GOMES FERREIRA',
        //       cpf: '16743141755',
        //       crm_number: '18385',
        //       crm_state: 'ES',
        //       phone: '22999949722',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'DANIELE SCHERRER DE ABREU',
        //       cpf: '11221137735',
        //       crm_number: '10436',
        //       crm_state: 'ES',
        //       phone: '27998612545',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'MARCELA MARIA SOARES RIBEIRO',
        //       cpf: '10178831735',
        //       crm_number: '012912',
        //       crm_state: 'ES',
        //       phone: '27999400715',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'THAYS FURTADO DIAS DA MATA',
        //       cpf: '11519441657',
        //       crm_number: '15073',
        //       crm_state: 'ES',
        //       phone: '27997384529',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'MARIANA PEREIRA PARTELE',
        //       cpf: '10257723773',
        //       crm_number: '11348',
        //       crm_state: 'ES',
        //       phone: '27999027060',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'ROBERTA FONSECA DE SOUZA',
        //       cpf: '03569755509',
        //       crm_number: '12629',
        //       crm_state: 'ES',
        //       phone: '27981290022',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'POLYANNA MENDONCA DE QUEIROZ',
        //       cpf: '12464126756',
        //       crm_number: '013805',
        //       crm_state: 'ES',
        //       phone: '27996405103',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'CRISTIANA PITTELLA CASSINO',
        //       cpf: '14885277760',
        //       crm_number: '17416',
        //       crm_state: 'ES',
        //       phone: '27996439879',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'HELOISA ROCHA RIBEIRO',
        //       cpf: '12175401642',
        //       crm_number: '18430',
        //       crm_state: 'ES',
        //       phone: '33998784799',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'ADRIANA GOULART AGUIAR',
        //       cpf: '12011246709',
        //       crm_number: '17496',
        //       crm_state: 'ES',
        //       phone: '21972243009',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: '6ab49e2b-7785-4eb1-abdb-a50db0f13f1e',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MAT - Maternidade Pr\u00f3 Matre',
        //       physician_name: 'JUANA ZANCHETTA VIEIRA',
        //       cpf: '11389406741',
        //       crm_number: '12281',
        //       crm_state: 'ES',
        //       phone: '27999065128',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //     {
        //       workplace_id: 'c5d0f0f8-1545-432b-bd2f-9870241513a7',
        //       institution_name: 'HYGEA GESTAO & SAUDE LTDA',
        //       workplace_name: 'MPF - Maternidade Papa Francisco',
        //       physician_name: 'ANA CAROLINA STAATS',
        //       cpf: '00901265993',
        //       crm_number: '48406',
        //       crm_state: 'PR',
        //       phone: '41998420200',
        //       classification_color: 'red',
        //       classification_label: 'N\u00e3o confirmado',
        //       workplace_first_advance_at: null,
        //       workplace_last_advance_at: null,
        //       anticipations_current_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_previous_month: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       anticipations_two_months_ago: {
        //         count: 0,
        //         value: 0,
        //         breakdown: [],
        //       },
        //       recurrence: 'Nunca Antecipou',
        //       competence: '2026-08',
        //     },
        //   ],
        // }.data.map(item => ({ ...item, row_key: `${item.workplace_id}_${item.cpf}` }))
        items.value = (response.data.data ?? []).map(item => ({ ...item, row_key: `${item.workplace_id}_${item.cpf}` }))
      })
      .finally(() => {
        loading.value = false
      })
  }

  const downloadReport = () => {
    store.dispatch('app-reports/monthlyProfessionalWorkplaceReportExport', getParams())
      .then(response => {
        downloadBlob(null, response)
      })
  }

  return {
    loading,
    filterYear,
    filterMonth,
    yearOptions,
    monthOptions,
    institutionId,
    institutionOptions,
    fetchInstitutions,
    items,
    tableColumns,
    getClassificationColor,
    loadReport,
    downloadReport,
    truncateString,
    formatCpf,
    formatCurrencyInt,
    formatCompetence,
  }
}
